import React, { useEffect, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { FiClock } from 'react-icons/fi';
import { authService } from '../services/authService';
import { clearLocalAuthSession } from '../supabaseClient';
import {
  getMarketingSignInUrl,
  markLogoutReason,
  SESSION_EXPIRED_REASON,
} from '../utils/authRedirect';
import {
  evaluateSessionTimeout,
  getSessionTrackingKeys,
  readStoredTimestamp,
  SESSION_TIMEOUT_POLICY,
  type SessionExpiryReason,
} from '../utils/sessionTimeout';
import { useI18n } from '../i18n';
import './SessionTimeout.css';

interface SessionTimeoutProps {
  session: Session;
}

interface WarningState {
  reason: SessionExpiryReason;
  remainingSeconds: number;
}

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'keydown',
  'pointerdown',
  'touchstart',
  'wheel',
];

const getSessionId = (session: Session): string => {
  try {
    const payload = session.access_token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const decoded = JSON.parse(atob(padded)) as { session_id?: string };
    return decoded.session_id ?? session.user.id;
  } catch {
    return session.user.id;
  }
};

const formatRemainingTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export default function SessionTimeout({ session }: SessionTimeoutProps) {
  const { t } = useI18n();
  const [warning, setWarning] = useState<WarningState | null>(null);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const warningRef = useRef<WarningState | null>(null);
  const isExpiringRef = useRef(false);
  const trackingKeys = getSessionTrackingKeys(getSessionId(session));

  useEffect(() => {
    warningRef.current = warning;
  }, [warning]);

  useEffect(() => {
    const now = Date.now();
    const startedAt = readStoredTimestamp(trackingKeys.startedAt, now);
    const lastActivityAt = readStoredTimestamp(trackingKeys.lastActivity, now);

    localStorage.setItem(trackingKeys.startedAt, String(startedAt));
    localStorage.setItem(trackingKeys.lastActivity, String(lastActivityAt));

    const expireSession = async () => {
      if (isExpiringRef.current) return;

      isExpiringRef.current = true;
      setIsSigningOut(true);
      markLogoutReason(SESSION_EXPIRED_REASON);

      try {
        await authService.signOut();
      } catch {
        clearLocalAuthSession();
      } finally {
        localStorage.removeItem(trackingKeys.startedAt);
        localStorage.removeItem(trackingKeys.lastActivity);
        window.location.replace(getMarketingSignInUrl(SESSION_EXPIRED_REASON));
      }
    };

    const recordActivity = () => {
      if (isExpiringRef.current || warningRef.current) return;

      localStorage.setItem(trackingKeys.lastActivity, String(Date.now()));
      setWarning(null);
    };

    const checkTimeout = () => {
      if (isExpiringRef.current) return;

      const checkedAt = Date.now();
      const currentStartedAt = readStoredTimestamp(trackingKeys.startedAt, checkedAt);
      const currentLastActivityAt = readStoredTimestamp(trackingKeys.lastActivity, checkedAt);
      const status = evaluateSessionTimeout(
        checkedAt,
        currentLastActivityAt,
        currentStartedAt,
        SESSION_TIMEOUT_POLICY,
      );

      if (status.expired) {
        void expireSession();
        return;
      }

      if (status.shouldWarn) {
        const nextWarning = {
          reason: status.reason,
          remainingSeconds: Math.max(1, Math.ceil(status.remainingMs / 1000)),
        };
        warningRef.current = nextWarning;
        setWarning(nextWarning);
        return;
      }

      warningRef.current = null;
      setWarning(null);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkTimeout();
    };
    const handleStorage = (event: StorageEvent) => {
      if (
        event.key === trackingKeys.lastActivity
        || event.key === trackingKeys.startedAt
      ) {
        if (event.newValue === null) {
          void expireSession();
          return;
        }
        checkTimeout();
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, recordActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorage);

    checkTimeout();
    const intervalId = window.setInterval(checkTimeout, 1000);

    return () => {
      window.clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, recordActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, [session, trackingKeys.lastActivity, trackingKeys.startedAt]);

  const staySignedIn = () => {
    localStorage.setItem(trackingKeys.lastActivity, String(Date.now()));
    warningRef.current = null;
    setWarning(null);
  };

  const signOutNow = async () => {
    if (isExpiringRef.current) return;
    isExpiringRef.current = true;
    setIsSigningOut(true);
    markLogoutReason(SESSION_EXPIRED_REASON);

    try {
      await authService.signOut();
    } catch {
      clearLocalAuthSession();
    } finally {
      window.location.replace(getMarketingSignInUrl(SESSION_EXPIRED_REASON));
    }
  };

  if (!warning) return null;

  const isAbsoluteLimit = warning.reason === 'absolute';

  return (
    <div className="session-timeout" role="presentation">
      <div
        className="session-timeout__dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-timeout-title"
        aria-describedby="session-timeout-description"
        onKeyDown={(event) => {
          if (event.key === 'Tab') {
            event.preventDefault();
            event.currentTarget.querySelector('button')?.focus();
          }
        }}
      >
        <div className="session-timeout__icon">
          <FiClock aria-hidden="true" />
        </div>
        <div className="session-timeout__eyebrow">{t('Session security')}</div>
        <h2 id="session-timeout-title">
          {isAbsoluteLimit ? t('Your session is ending') : t('Are you still working?')}
        </h2>
        <p id="session-timeout-description">
          {isAbsoluteLimit
            ? t('For security, you must sign in again after the maximum session duration.')
            : t('You will be signed out automatically because there has been no activity.')}
        </p>
        <div className="session-timeout__countdown" aria-live="polite">
          {formatRemainingTime(warning.remainingSeconds)}
        </div>
        <div className="session-timeout__actions">
          {isAbsoluteLimit ? (
            <button
              type="button"
              className="btn btn--primary"
              onClick={signOutNow}
              disabled={isSigningOut}
              autoFocus
            >
              {isSigningOut ? t('Signing out...') : t('Sign in again')}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn--primary"
              onClick={staySignedIn}
              disabled={isSigningOut}
              autoFocus
            >
              {t('Stay signed in')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
